export type DeployEnv = "production" | "local";

function validateDeployEnv(value: unknown): DeployEnv {
  const normalized = (typeof value === "string" ? value : "").toLowerCase().trim();

  if (!normalized || normalized === "production") {
    return "production";
  }

  if (normalized === "local") {
    return "local";
  }

  throw new Error(`Invalid VITE_DEPLOY_ENV: "${String(value)}". Must be "production" or "local".`);
}

function requireEnvString(value: unknown, name: string): string {
  const normalized = (typeof value === "string" ? value : "").trim();

  if (!normalized) {
    throw new Error(`Missing required environment variable: ${name}`);
  }

  return normalized;
}

function optionalEnvString(value: unknown): string {
  return (typeof value === "string" ? value : "").trim();
}

function validateAuthDomain(value: unknown): string {
  const authDomain = requireEnvString(value, "VITE_IDENTITY_PLATFORM_AUTH_DOMAIN");

  if (/^https?:\/\//i.test(authDomain)) {
    throw new Error(
      `Invalid VITE_IDENTITY_PLATFORM_AUTH_DOMAIN: provide only the host, not a URL. Got: "${authDomain}"`
    );
  }

  if (authDomain.includes("/") || authDomain.includes("?") || authDomain.includes("#")) {
    throw new Error(
      `Invalid VITE_IDENTITY_PLATFORM_AUTH_DOMAIN: must not include a path, query, or fragment. Got: "${authDomain}"`
    );
  }

  if (authDomain.includes(":")) {
    throw new Error(
      `Invalid VITE_IDENTITY_PLATFORM_AUTH_DOMAIN: must not include a port. Use VITE_FIREBASE_AUTH_EMULATOR_URL for local emulator ports. Got: "${authDomain}"`
    );
  }

  return authDomain;
}

function validateBooleanFlag(value: unknown, name: string): boolean {
  const normalized = (typeof value === "string" ? value : "").toLowerCase().trim();

  if (!normalized || normalized === "false") {
    return false;
  }

  if (normalized === "true") {
    return true;
  }

  throw new Error(`Invalid ${name}: must be "true" or "false". Got: "${String(value)}"`);
}

function validatePositiveMinutes(value: unknown, name: string, defaultValue: number): number {
  const normalized = optionalEnvString(value);
  if (!normalized) return defaultValue;

  const minutes = Number(normalized);
  if (!Number.isFinite(minutes) || minutes <= 0 || minutes * 60_000 > 2_147_483_647) {
    throw new Error(
      `Invalid ${name}: must be a positive number of minutes within the timer limit.`
    );
  }

  return minutes;
}

function validateEmulatorUrl(value: unknown, deployEnv: DeployEnv): string {
  const emulatorUrl = optionalEnvString(value);

  if (!emulatorUrl) {
    return "";
  }

  if (deployEnv === "production") {
    throw new Error(
      "Configuration error: VITE_FIREBASE_AUTH_EMULATOR_URL must not be set when VITE_DEPLOY_ENV is production."
    );
  }

  if (!emulatorUrl.startsWith("http://")) {
    throw new Error("Invalid VITE_FIREBASE_AUTH_EMULATOR_URL: must use http:// protocol.");
  }

  try {
    new URL(emulatorUrl);
  } catch {
    throw new Error(`Invalid VITE_FIREBASE_AUTH_EMULATOR_URL: "${emulatorUrl}"`);
  }

  return emulatorUrl;
}

const deployEnv = validateDeployEnv(import.meta.env.VITE_DEPLOY_ENV);
const firebaseAuthEmulatorUrl = validateEmulatorUrl(
  import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL,
  deployEnv
);
const useAppOriginForEmulator = validateBooleanFlag(
  import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_USE_APP_ORIGIN,
  "VITE_FIREBASE_AUTH_EMULATOR_USE_APP_ORIGIN"
);
const historicalPlaybackMaxPlayMinutes = validatePositiveMinutes(
  import.meta.env.VITE_HISTORICAL_PLAYBACK_AUTO_PAUSE_MINUTES,
  "VITE_HISTORICAL_PLAYBACK_AUTO_PAUSE_MINUTES",
  10
);

if (useAppOriginForEmulator && deployEnv !== "local") {
  throw new Error(
    "Configuration error: VITE_FIREBASE_AUTH_EMULATOR_USE_APP_ORIGIN can only be true when VITE_DEPLOY_ENV=local."
  );
}

if (useAppOriginForEmulator && !firebaseAuthEmulatorUrl) {
  throw new Error(
    "Configuration error: VITE_FIREBASE_AUTH_EMULATOR_USE_APP_ORIGIN requires VITE_FIREBASE_AUTH_EMULATOR_URL."
  );
}

export const env = {
  VITE_DEPLOY_ENV: deployEnv,
  VITE_IDENTITY_PLATFORM_API_KEY: requireEnvString(
    import.meta.env.VITE_IDENTITY_PLATFORM_API_KEY,
    "VITE_IDENTITY_PLATFORM_API_KEY"
  ),
  VITE_IDENTITY_PLATFORM_AUTH_DOMAIN: validateAuthDomain(
    import.meta.env.VITE_IDENTITY_PLATFORM_AUTH_DOMAIN
  ),
  VITE_IDENTITY_PLATFORM_PROJECT_ID: requireEnvString(
    import.meta.env.VITE_IDENTITY_PLATFORM_PROJECT_ID,
    "VITE_IDENTITY_PLATFORM_PROJECT_ID"
  ),
  VITE_IDENTITY_PLATFORM_DEFAULT_TENANT_ID: optionalEnvString(
    import.meta.env.VITE_IDENTITY_PLATFORM_DEFAULT_TENANT_ID
  ),
  VITE_PLATFORM_API_URL: requireEnvString(
    import.meta.env.VITE_PLATFORM_API_URL,
    "VITE_PLATFORM_API_URL"
  ),
  VITE_FIREBASE_AUTH_EMULATOR_URL: firebaseAuthEmulatorUrl,
  VITE_FIREBASE_AUTH_EMULATOR_USE_APP_ORIGIN: useAppOriginForEmulator,
  VITE_HISTORICAL_PLAYBACK_AUTO_PAUSE_MINUTES: historicalPlaybackMaxPlayMinutes,
} as const;
